import type { SubmitHandler } from 'react-hook-form';
import { useState } from 'react';
import {
  faBan,
  faBell,
  faCheck,
  faFaceAngry,
  faFileLines,
  faFilePdf,
  faMicrochipAi,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, IconButton, Stack, Text } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import stylex from '@stylexjs/stylex';
import { DateTime } from 'luxon';
import { Trans, useTranslation } from 'react-i18next';

import type { ITableRowProps } from '~/components/molecules/Table/TableRow';
import type { IUserContent } from '~/lib/api/modules/UserContent';
import { Link } from '~/components/atoms/Link';
import { QuerySkeleton } from '~/components/atoms/QuerySkeleton';
import { Thumbnail } from '~/components/atoms/Thumbnail';
import { UserContentReviewModerateFormDialog } from '~/components/forms/UserContentReviewModerateForm';
import {
  IUserContentReviewNotifyFormValues,
  UserContentReviewNotifyFormDialog,
} from '~/components/forms/UserContentReviewNotifyForm';
import { Table } from '~/components/molecules/Table';
import { parseUserInfo } from '~/helpers/parseUserInfo';
import { useDocumentsQueries } from '~/lib/api/modules/Documents/hooks/useDocumentsQueries';
import { useNotifyUserContentMutation } from '~/lib/api/modules/UserContent/hook/useNotifyUserContentMutation';
import { useReviewUserContentMutation } from '~/lib/api/modules/UserContent/hook/useReviewUserContentMutation';
import { ErrorMessage } from '../ErrorMessage';

export type IUserContentReviewListItemProps = ITableRowProps & {
  appId: string;
  userContent: IUserContent;
};

const styles = stylex.create({
  articleIcon: {
    marginRight: '0.25rem',
  },
  content: {
    fontStyle: 'italic',
  },
  moderated: {
    color: colorSchemeTokens.outline,
  },
});

export const UserContentReviewListItem: React.FC<
  IUserContentReviewListItemProps
> = (props) => {
  const { appId, userContent, ...other } = props;
  const { t, i18n } = useTranslation();
  const reviewUserContentMutation = useReviewUserContentMutation();
  const notifyUserContentMutation = useNotifyUserContentMutation();
  const [validating, setValidating] = useState(false);
  const [moderating, setModerating] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [
    userContentReviewModerateFormDialogOpened,
    setUserContentReviewModerateFormDialogOpened,
  ] = useState(false);
  const [
    userContentReviewNotifyFormDialogOpened,
    setUserContentReviewNotifyFormDialogOpened,
  ] = useState(false);

  const thumbnailUrl = userContent.pictures?.[0]?.thumbUrl;
  const locale = i18n.language;
  const userInfo = userContent.user
    ? parseUserInfo(userContent.user)
    : undefined;
  const humanReadableCreatedAt = userContent.createdAt
    ? DateTime.fromISO(userContent.createdAt).toLocaleString(
        DateTime.DATETIME_SHORT,
        { locale },
      )
    : undefined;
  const mutating =
    reviewUserContentMutation.isLoading || notifyUserContentMutation.isLoading;

  const aiModerationStatus = userContent.offensive?.status || 'unchecked';

  const pdfsQueries = useDocumentsQueries(
    userContent.type === 'article'
      ? userContent.data?.pdfs?.map((documentId) => ({
          appId,
          documentId,
        }))
      : undefined,
  );

  const handleReview = (validated: boolean, reason?: string): void => {
    if (validated) {
      setValidating(true);
    } else {
      setModerating(true);
    }

    void reviewUserContentMutation
      .mutateAsync({
        appId,
        contentId: userContent._id,
        body: {
          moderated: !validated,
          reason,
        },
      })
      .catch(() => {
        // TODO: handle error (snackbar)
      })
      .finally(() => {
        setValidating(false);
        setModerating(false);
      });
  };

  const handleNotify: SubmitHandler<IUserContentReviewNotifyFormValues> = (
    values,
  ) => {
    setNotifying(true);

    void notifyUserContentMutation
      .mutateAsync({
        appId,
        contentId: userContent._id,
        body: {
          notifyAt: values.date!,
          title: values.title,
          content: values.content,
        },
      })
      .catch(() => {
        // TODO: handle error (snackbar)
      })
      .finally(() => {
        setNotifying(false);
      });
  };

  return (
    <>
      <UserContentReviewModerateFormDialog
        opened={userContentReviewModerateFormDialogOpened}
        onClose={() => setUserContentReviewModerateFormDialogOpened(false)}
        onSubmit={(values) => handleReview(false, values.reason)}
      />

      <UserContentReviewNotifyFormDialog
        opened={userContentReviewNotifyFormDialogOpened}
        onClose={() => setUserContentReviewNotifyFormDialogOpened(false)}
        onSubmit={handleNotify}
      />

      <Table.Row {...other}>
        {userContent.type === 'article' ? (
          <Stack
            as={Table.BodyAction}
            horizontal
            gap={3}
            href={''}
            sx={userContent.moderated && styles.moderated}
          >
            <Thumbnail url={thumbnailUrl} size="lg" />

            <Stack gap={1}>
              <Text variant="title" size="sm" truncateLines={2}>
                <FontAwesomeIcon
                  {...stylex.props(styles.articleIcon)}
                  icon={faFileLines}
                />
                {userContent.data?.title}
              </Text>
              <div>
                «&nbsp;
                <Trans
                  {...stylex.props(styles.content)}
                  i18nKey="UserContentReviewListItem.content"
                  values={{
                    content: userContent.data?.content,
                  }}
                />
                &nbsp;»
              </div>
              <Trans
                parent="div"
                i18nKey="UserContentReviewListItem.by"
                values={{
                  createdAt: humanReadableCreatedAt,
                  authorName: userInfo?.fullName,
                }}
              />
              {userContent.data?.pdfs?.length && (
                <Stack gap={1}>
                  {pdfsQueries.map((pdfQuery, pdfQueryIndex) => (
                    <QuerySkeleton
                      key={pdfQueryIndex}
                      query={pdfQuery}
                      length={{ min: 16, max: 48 }}
                    >
                      <Link to={pdfQuery.data?.url ?? '#'} target="_blank">
                        <FontAwesomeIcon icon={faFilePdf} />{' '}
                        {pdfQuery.data?.name}
                      </Link>
                    </QuerySkeleton>
                  ))}
                </Stack>
              )}
            </Stack>
          </Stack>
        ) : userContent.type === 'comment' ? (
          <Stack
            as={Table.Body}
            gap={1}
            sx={userContent.moderated && styles.moderated}
          >
            <Trans
              {...stylex.props(styles.content)}
              parent="div"
              i18nKey="UserContentReviewListItem.content"
              values={{
                content: userContent.data,
              }}
            />
          </Stack>
        ) : (
          <Table.Body>
            <ErrorMessage>
              Unsupported user content type: {userContent.type}
            </ErrorMessage>
          </Table.Body>
        )}

        <Table.Body>
          {userContent.reportsCount ? (
            <Stack horizontal gap={1}>
              <FontAwesomeIcon icon={faFaceAngry} />
              {userContent.reportsCount}
            </Stack>
          ) : null}
        </Table.Body>

        <Stack as={Table.Body} horizontal gap={3} justify="end">
          {userContent.type === 'article' &&
            userContent.reviewed &&
            !userContent.moderated && (
              <Button
                variant="text"
                icon={<FontAwesomeIcon icon={faBell} />}
                onClick={() => setUserContentReviewNotifyFormDialogOpened(true)}
                disabled={mutating}
                loading={notifying}
              >
                {t('UserContentReviewListItem.notify')}
              </Button>
            )}

          {aiModerationStatus === 'checking' ? (
            <FontAwesomeIcon icon={faMicrochipAi} color="blue" />
          ) : aiModerationStatus === 'offensive' ? (
            <FontAwesomeIcon icon={faMicrochipAi} color="red" />
          ) : aiModerationStatus === 'inoffensive' ? (
            <FontAwesomeIcon icon={faMicrochipAi} color="green" />
          ) : (
            false
          )}

          <IconButton
            icon={<FontAwesomeIcon icon={faCheck} />}
            onClick={() => handleReview(true)}
            loading={mutating && validating}
            disabled={mutating}
            variant={
              !mutating && userContent.reviewed && !userContent.moderated
                ? 'filled'
                : 'standard'
            }
            toggle
            selected={userContent.reviewed && !userContent.moderated}
          />
          <IconButton
            icon={<FontAwesomeIcon icon={faBan} />}
            onClick={() => setUserContentReviewModerateFormDialogOpened(true)}
            loading={mutating && moderating}
            disabled={mutating}
            variant={
              !mutating && userContent.reviewed && userContent.moderated
                ? 'filled'
                : 'standard'
            }
            toggle
            selected={userContent.reviewed && userContent.moderated}
          />
        </Stack>
      </Table.Row>
    </>
  );
};
